<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { FormGroup, FormSchema } from '@modules/forms/components/VForm/types';
import type {
  ISettingsBookkeepingGetItemPayload,
  ISettingsBookkeepingItemCreated,
} from '@modules/general-settings/submodules/bookkeeping/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useFormatDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import {
  ADMIN_GENERAL_SETTINGS_BOOKKEEPING_LIST_ROUTE_NAME,
  ADMIN_GENERAL_SETTINGS_BOOKKEEPING_UPDATE_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING,
} from '@modules/general-settings/submodules/bookkeeping/constants';
import { isSettingsBookkeepingGetItemPayload } from '@modules/general-settings/submodules/bookkeeping/guards/guards';
import { useSettingsBookkeepingUpdateMutation } from '@modules/general-settings/submodules/bookkeeping/mutations';
import {
  useSettingsBookkeepingItemQuery,
} from '@modules/general-settings/submodules/bookkeeping/queries';
import { useI18n } from '@modules/i18n/composables';
import { useToastStore } from '@modules/toast/store';
import { utcDateToLocal } from '@utils/date';
import { assert } from '@utils/guards';

const { getTranslation } = useI18n();
const { formatDate } = useFormatDate();

const route = useRoute();
const router = useRouter();
const { displayPresetToast } = useToastStore();

function handleError(): void {
  void router.push({ name: ADMIN_GENERAL_SETTINGS_BOOKKEEPING_LIST_ROUTE_NAME });
  displayPresetToast('error');
}

const currentMerchantId = computed(() => (Number.parseInt(String(route.params.merchantId), 10)));

const getItemPayload = computed<ISettingsBookkeepingGetItemPayload | undefined>(() => {
  const payload = { id: Number.parseInt(String(route.params.itemId), 10), merchantId: currentMerchantId.value };
  const isValidState = route.name === ADMIN_GENERAL_SETTINGS_BOOKKEEPING_UPDATE_ROUTE_NAME;
  if (!isValidState)
    return;

  try {
    assert(isSettingsBookkeepingGetItemPayload(payload));

    return payload;
  } catch (e) {
    handleError();
    throw e;
  }
});

const { data, isLoading } = useSettingsBookkeepingItemQuery(getItemPayload);
const { mutateAsync: updateItem, errors, isLoading: isLoadingSubmission } = useSettingsBookkeepingUpdateMutation();

const formSchema = computed<FormSchema<ISettingsBookkeepingItemCreated>>(() => ({
  id: {
    type: EFormInputTypes.HIDDEN,
    value: data.value?.history_of_limit_change.id,
    hideErrors: true,
  },
  limit: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'HISTORY_FORM_LIMIT').value,
    required: true,
    value: data.value?.history_of_limit_change.limit,
  },
  date: {
    type: EFormInputTypes.DATE,
    view: 'month',
    dateFormat: 'mm.yy',
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'HISTORY_FORM_DATE').value,
    required: true,
    value: data.value?.history_of_limit_change.date ? utcDateToLocal(data.value.history_of_limit_change.date) : undefined,
  },
}));

const group = computed<FormGroup<ISettingsBookkeepingItemCreated>[]>(() => ([{
  items: ['id', 'limit', 'date'],
  isLoading: isLoading.value,
}]));

function handleSubmit(values: ISettingsBookkeepingItemCreated): void {
  void updateItem({
    merchant: currentMerchantId.value,
    history_of_limit_change: {
      id: values.id,
      limit: values.limit,
      date: formatDate(values.date, 'MM.yyyy'),
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsBookkeepingUpdatePage'"
    type="default"
    :title="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'HISTORY_HEADER')"
  >
    <VLoader :loading="isLoading">
      <VForm
        :schema="formSchema"
        :groups="group"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'HISTORY_SAVE')"
        :custom-errors="errors"
        :is-loading="isLoadingSubmission"
        @submit="handleSubmit"
      >
        <template #secondaryBtn>
          <VBackButton
            :to="{ name: ADMIN_GENERAL_SETTINGS_BOOKKEEPING_LIST_ROUTE_NAME }"
            :label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'HISTORY_BACK')"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
